import { formatReportType, STATUS_ENUM } from 'constants/report';

import * as React from 'react';
import { useMemo, useState } from 'react';

import { QaPreviewProject } from '@graphql/queries/QaPreviewProjectsQuery';
import { QaPreviewReportJob } from '@graphql/queries/QaPreviewReportQuery';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import {
  Alert,
  Autocomplete,
  Button,
  FormControlLabel,
  IconButton,
  Paper,
  Switch,
  TextField,
  Tooltip,
} from '@mui/material';
import ApprovalModal from 'containers/ReportQAList/ApprovalModal';
import { QaStatusChip } from 'containers/ReportQAList/QaStatusChip';
import formatDate from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import { useTranslation } from 'react-i18next';

import styles from './ReportQAPreview.module.css';

export type QaDecision = 'approve' | 'approveAndAdd' | 'reject' | 'reopen';

interface ContextOption {
  id: number | null;
  label: string;
  reportCount?: number;
  containsReport?: boolean;
}

interface Props {
  report: QaPreviewReportJob;
  projects: QaPreviewProject[];
  projectsLoading: boolean;
  projectId: number | null;
  mapOnly: boolean;
  busy: boolean;
  onBack: () => void;
  onProjectChange: (project: number | 'standalone') => void;
  onMapOnlyChange: (mapOnly: boolean) => void;
  onDecision: (decision: QaDecision) => void;
}

const QaPreviewToolbar: React.FC<Props> = ({
  report,
  projects,
  projectsLoading,
  projectId,
  mapOnly,
  busy,
  onBack,
  onProjectChange,
  onMapOnlyChange,
  onDecision,
}) => {
  const { t } = useTranslation();
  const [pendingApproval, setPendingApproval] = useState<QaDecision | null>(
    null,
  );
  const [showDemographics, setShowDemographics] = useState(false);

  const options = useMemo<ContextOption[]>(
    () => [
      { id: null, label: t('reportsQa.preview.standalone') },
      ...projects.map((p) => ({
        id: p.id,
        label: p.name,
        reportCount: p.reports.totalCount,
        containsReport: p.containsReport.totalCount > 0,
      })),
    ],
    [projects, t],
  );
  const selected = options.find((o) => o.id === projectId) ?? options[0];

  const isSuccess = report.reportStatus === STATUS_ENUM.SUCCESS;
  const awaitingQa = isSuccess && report.passedQa === null;
  const selectedProject = projectId ? selected : null;
  const canAddToProject = !!selectedProject && !selectedProject.containsReport;

  return (
    <Paper className={styles.toolbar} elevation={3}>
      <div className={styles.toolbarRow}>
        <div className={styles.titleBlock}>
          <Tooltip title={t('reportsQa.preview.back')}>
            <IconButton onClick={onBack} size="small">
              <KeyboardBackspaceIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <h2 className={styles.title}>{report.projectName}</h2>
          <QaStatusChip
            reportStatus={report.reportStatus}
            passedQa={report.passedQa}
            mapOnly={report.mapOnly}
          />
        </div>
        <div className={styles.actions}>
          <Button size="small" onClick={() => setShowDemographics(true)}>
            {t('reports.demographics')}
          </Button>
          {awaitingQa && (
            <>
              <Button
                variant="outlined"
                color="error"
                disabled={busy}
                onClick={() => onDecision('reject')}
              >
                {t('reportsQa.preview.reject')}
              </Button>
              <Button
                variant={canAddToProject ? 'outlined' : 'contained'}
                color="secondary"
                disableElevation
                disabled={busy}
                onClick={() => setPendingApproval('approve')}
              >
                {mapOnly
                  ? t('reportsQa.preview.approveMapOnly')
                  : t('reportsQa.preview.approve')}
              </Button>
              {canAddToProject && (
                <Button
                  variant="contained"
                  color="secondary"
                  disableElevation
                  disabled={busy}
                  onClick={() => setPendingApproval('approveAndAdd')}
                >
                  {t('reportsQa.preview.approveAndAdd', {
                    project: selectedProject.label,
                  })}
                </Button>
              )}
            </>
          )}
          {isSuccess && report.passedQa !== null && (
            <Tooltip
              title={
                report.passedQa && report.noOfProjectsAddedTo > 0
                  ? t('reportsQa.preview.reopenBlocked')
                  : ''
              }
            >
              <span>
                <Button
                  variant="outlined"
                  disabled={
                    busy ||
                    (report.passedQa === true && report.noOfProjectsAddedTo > 0)
                  }
                  onClick={() => onDecision('reopen')}
                >
                  {t('reportsQa.preview.reopen')}
                </Button>
              </span>
            </Tooltip>
          )}
        </div>
      </div>

      <div className={styles.meta}>
        <span>
          <span className={styles.metaLabel}>{t('general.workspace')}:</span>
          {report.producerByWorkspaceId?.name}
        </span>
        <span>
          <span className={styles.metaLabel}>{t('reports.reportType')}:</span>
          {formatReportType(report.reportType)}
        </span>
        <span>
          <span className={styles.metaLabel}>{t('reports.targetGroup')}:</span>
          {report.targetGroupName}
        </span>
        <span>
          <span className={styles.metaLabel}>{t('reports.submitter')}:</span>
          {report.requestedThrough}
        </span>
        {report.startedAt && (
          <span>
            <span className={styles.metaLabel}>
              {t('reports.submittedOn')}:
            </span>
            {formatDate(parseISO(report.startedAt), 'yyyy-MM-dd HH:mm')}
          </span>
        )}
      </div>

      <div className={styles.toolbarRow}>
        <div className={styles.controls}>
          <Autocomplete
            className={styles.contextSelect}
            size="small"
            disableClearable
            loading={projectsLoading}
            options={options}
            value={selected}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            getOptionLabel={(o) => o.label}
            onChange={(_, option) => onProjectChange(option.id ?? 'standalone')}
            renderOption={(props, option) => (
              <li {...props} key={option.id ?? 'standalone'}>
                <div className={styles.optionRow}>
                  <span>{option.label}</span>
                  {option.id !== null && (
                    <span className={styles.optionMeta}>
                      {option.containsReport
                        ? t('reportsQa.preview.alreadyInProject')
                        : t('reportsQa.preview.reportCount', {
                            count: option.reportCount,
                          })}
                    </span>
                  )}
                </div>
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label={t('reportsQa.preview.context')} />
            )}
          />
          <FormControlLabel
            control={
              <Switch
                checked={mapOnly}
                color="secondary"
                onChange={(e) => onMapOnlyChange(e.target.checked)}
              />
            }
            label={t('reportsQa.preview.mapOnly')}
          />
        </div>
      </div>

      {awaitingQa && (
        <Alert severity="info" variant="outlined">
          {t('reportsQa.preview.hiddenFromClients')}
        </Alert>
      )}

      <ApprovalModal
        reportId={showDemographics || pendingApproval ? report.reportId : null}
        displayOnly={showDemographics}
        cancelButtonText={t('general.cancel')}
        cancelFunction={() => {
          setShowDemographics(false);
          setPendingApproval(null);
        }}
        confirmButtonText={
          pendingApproval === 'approveAndAdd' && selectedProject
            ? t('reportsQa.preview.approveAndAdd', {
                project: selectedProject.label,
              })
            : t('general.confirm')
        }
        confirmFunction={() => {
          const decision = pendingApproval;
          setPendingApproval(null);
          if (decision) onDecision(decision);
        }}
      />
    </Paper>
  );
};

export default QaPreviewToolbar;
